import React, { useEffect, useRef } from "react";
import styles from "./Race.module.css";
import "../Common.css";
import mainStyles from "racing-renderings-library/src/Components/Charts.module.css";
import DataBy from "racing-renderings-library/src/Components/DataBy/DataBy";
import ControlButtons from "racing-renderings-library/src/Components/ControlBtns/ControlBtns";
import {
  applyBrightness,
  createHiDPICanvas,
  getGridPattern,
  getLineObj,
  getRowsPattern,
  lineSegmentPoint,
  msToTime,
  onResize,
  ratio,
  updSTATE,
} from "racing-renderings-library/src/canvasesUtils";
import {
  getCoords,
  getResized,
  getWidths,
  isTouch,
  loadImage,
  record as recordUtil,
} from "racing-renderings-library/src/utils";
import legendImg from "racing-renderings-library/assets/images/chart/png/legend.png";
import ShareBtns from "../ShareBtns/ShareBtns";
import positionTypeImg from "../../../assets/images/common/icons/cupBtn.svg";
import laptimeTypeImg from "../../../assets/images/common/icons/timeBtn.svg";
import CustomizeBlock from "../CustomizeBlock/CustomizeBlock";
import { recordWidget } from "../../utils";

const POSITION = "POSITION";
const LAPTIME = "LAPTIME";

const Race = ({ race, icons, t, logo, theme, initType, ...props }) => {
  // race.drivers = race.chartData
  race.time = race.raceTime;

  const rowsRef = useRef();
  const redHeadRef = useRef();
  const leftRef = useRef();
  const fieldRef = useRef();
  const rightRef = useRef();
  const canvasesDivRef = useRef();
  const ctxs = {};

  const dataByRef = useRef();
  const playBtnRef = useRef();
  const zoomInBtnRef = useRef();
  const zoomOutBtnRef = useRef();
  const speedUpBtnRef = useRef();
  const speedDownBtnRef = useRef();

  const legendImgRef = useRef();
  const recordingSpanRef = useRef();

  let mainColor = theme !== undefined ? theme.mainColor : props.color;
  let BGColor = theme !== undefined ? theme.BGColor : "#ffffff";
  let textColor = theme !== undefined ? theme.textColor : "#333333";
  let headerColor = theme !== undefined ? theme.headerColor : "#ffffff";
  let ffl = (theme?.font ?? "Gibson-Light") + ", sans-serif";
  let ffr = (theme?.font ?? "Gibson-regular") + ", sans-serif";

  const driversCount = race.drivers.length;
  let headHeight = 70;
  const timeLineY = 20;
  const timeLinePadding = 40;
  let delY = 30;
  const size = {
    height: headHeight + delY * driversCount,
    fieldHeight: delY * driversCount,
    lapWidth: 50,
    headLapsLineHeight: 30,
  };
  const selectedDrivers = [];
  for (let i = 1; i <= driversCount; i++) selectedDrivers.push(i);
  const getCarSize = () => ({
    width: (Math.round(delY * 0.5) * race.drivers[0].carImg.width) / race.drivers[0].carImg.height,
    height: Math.round(delY * 0.5),
  });
  let carSize = getCarSize();
  const getFlagSize = () => ({
    width: Math.round(delY / 1.2),
    height: Math.round(delY / 2),
  });
  let flagSize = getFlagSize();
  const getIconSize = () => Math.round(delY * 0.8);
  let iconSize = getIconSize();
  size.startWidth = 10 + iconSize + 5 + carSize.width + 5 + 10; // margin + tyre + margin + car + margin + startGrid
  size.finishWidth = 10 + 5 + carSize.width + 20; // finishGrid + margin + car + margin
  size.laptimeFinishWidth = 100 + carSize.width;
  const getGrid = (BGColor, w = 20, h = size.height - headHeight) => {
    const grid = document.createElement("canvas");
    grid.width = w;
    grid.height = h;
    const ctx = grid.getContext("2d");
    ctx.fillStyle = ctx.createPattern(getGridPattern(w / 2, BGColor), "repeat");
    ctx.fillRect(0, 0, w, h);
    return grid;
  };
  const getCheckSize = () => {
    return Math.floor(delY / 2);
  };
  let checkSize = getCheckSize();
  const getShadows = (w = 20, h = size.height - headHeight) => {
    const getShadow = (isLeft) => {
      const shadow = document.createElement("canvas");
      shadow.width = w;
      shadow.height = h;
      const ctx = shadow.getContext("2d");
      const gradient = ctx.createLinearGradient(0, 10, w, 10);
      gradient.addColorStop(isLeft ? 0 : 1, "rgba(0,0,0,0.2)");
      gradient.addColorStop(isLeft ? 1 : 0, "rgba(0,0,0,0)");
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, w, h);
      return shadow;
    };
    return { left: getShadow(false), right: getShadow(true) };
  };
  let shadows = getShadows();
  let STATERideXStart = 15 + iconSize + carSize.width;

  const updDriverPresentState = (timePassed) => {
    const progress = timePassed / STATE.driverPresent.animDuration;
    if (STATE.driverPresent.progress !== progress) {
      STATE.driverPresent.progress = progress;
      STATE.driverPresent.curName = driversCount * progress;
      STATE.rightCol.needRerender = true;
      drawLeftCol();
    }
  };
  const updTrafficLightState = (timePassed) => {
    const progress = timePassed / STATE.trafficLight.animDuration;
    if (STATE.trafficLight.progress !== progress) {
      STATE.trafficLight.progress = timePassed / STATE.trafficLight.animDuration;
      STATE.trafficLight.phase = Math.floor(timePassed / 500);
      if (progress === 1) drawLeftCol();
    }
  };
  const updRideState = (timePassed) => {
    const progress = timePassed / STATE.ride.animDuration;

    if (progress === 1 && STATE.ride.progress !== progress) {
      STATE.ride.progress = progress;
      drawLeftCol();
    }

    STATE.ride.progress = progress;
    STATE.ride.x =
      STATERideXStart +
      (size.startWidth - STATERideXStart + size.lapWidth * race.lapTotal + size.finishWidth - 20) * progress; // -20 = margin-right
    const curLaps = getLapNumber();
    if (curLaps.withCar.curLap !== STATE.ride.curLaps.withCar.curLap) STATE.rightCol.needRerender = true;
    STATE.ride.curLaps = curLaps;
    STATE.ride.curLaps.leftLap = STATE.ride.curLaps.withoutCar.curLap - Math.ceil(size.fieldWidth / size.lapWidth);

    const prevLapTime = race.time.lapTimes[STATE.ride.curLaps.withoutCar.curLap].time;
    const nextLapTime = race.time.lapTimes[STATE.ride.curLaps.withoutCar.nextLap].time;
    const del =
      (STATE.ride.x - size.startWidth - size.lapWidth * STATE.ride.curLaps.withoutCar.curLap - carSize.width) /
      size.lapWidth;
    STATE.ride.delLap = del;
    STATE.ride.curTime = Math.round(prevLapTime + (nextLapTime - prevLapTime) * del);
  };
  const updFinishState = (timePassed) => {
    const progress = timePassed / STATE.finish.animDuration;

    if (STATE.finish.progress !== progress) {
      STATE.finish.progress = progress;
      STATE.finish.rightCol.curName = driversCount * progress;
      STATE.rightCol.needRerender = true;

      let moveToGridProg = timePassed / STATE.finish.laptimeFinish.moveToGridDuration;
      STATE.finish.laptimeFinish.moveToGridProgress = Math.max(0, Math.min(1, moveToGridProg));

      let replaceProg =
        (timePassed - STATE.finish.laptimeFinish.moveToGridDuration) / STATE.finish.laptimeFinish.replaceDuration;
      STATE.finish.laptimeFinish.replaceProgress = Math.max(0, Math.min(1, replaceProg));
    }

    // stop animation
    if (progress === 1) {
      STATE.playing = false;
      if (playBtnRef.current !== null) playBtnRef.current.checked = false;
      forcedRedraw();
    }
  };
  const resetState = (timePassed = 0) => {
    STATE.timePassed = timePassed;
    updSTATE(STATE.timePassed, STATE);
  };
  const setFullAnimDur = () => {
    let fullAnimDur = 0;
    for (const phase of STATE.phases) {
      fullAnimDur += STATE[phase[0]].animDuration;
    }
    STATE.animDuration = fullAnimDur;
  };
  const STATE = {
    playing: true,
    recording: false,
    dragop: false,
    type: initType ? initType : POSITION,
    animDuration: 0,
    timePassed: 0,
    progress: 0,
    rightCol: {
      needRerender: true,
    },
    phases: [
      ["driverPresent", updDriverPresentState],
      ["trafficLight", updTrafficLightState],
      ["ride", updRideState],
      ["finish", updFinishState],
    ],
    driverPresent: {
      animDuration: driversCount * 250, // *250
      progress: 0,
      curName: 0,
    },
    trafficLight: {
      animDuration: 3500,
      progress: 0,
      phase: 0,
    },
    ride: {
      animDuration: 1000 * race.lapTotal,
      progress: 0,
      x: STATERideXStart,
      delLap: 0,
      curLaps: {
        leftLap: 0,
        withCar: { curLap: 0, nextLap: 0 },
        withoutCar: { curLap: 0, nextLap: 0 },
      },
      curTime: 0,
    },
    finish: {
      animDuration: 6000,
      progress: 0,
      rightCol: {
        curName: 0,
      },
      laptimeFinish: {
        moveToGridDuration: 1000,
        moveToGridProgress: 0,

        replaceDuration: 2000,
        replaceProgress: 0,
      },
    },
  };

  let grid = getGrid(BGColor);
  let speed = 0;
  let lastFrameDate, nextFrameID;
  let isLegendShown = false;
  let marginLeft, marginRight;
  let columnsX;

  const getColumnsX = () => {
    return {
      lpos: size.windowWidth > 1000 ? 15 : -100,
      lcheck: size.windowWidth > 1000 ? 55 : 10,
      lflag: size.windowWidth > 1000 ? 80 : -100,
      lname: size.windowWidth > 1000 ? 115 : 35,

      rname: size.windowWidth > 700 ? 10 : 999,
      rpos: size.windowWidth > 700 ? 80 : 10,
      rtime: size.windowWidth > 700 ? 120 : 999,
      rpts: 240,
      rpit: 290,
      rmove: 330,
    };
  };
  const setIncludedDriverAlpha = (ctx, position, value = 0.4) => {
    if (selectedDrivers.length === 0) ctx.globalAlpha = value;
    if (!selectedDrivers.includes(position)) ctx.globalAlpha = value;
    else ctx.globalAlpha = 1;
  };
  const presentSettings = (ctx, position, xOffset, isStart = true) => {
    const [progress, curName] = isStart
      ? [STATE.driverPresent.progress, STATE.driverPresent.curName]
      : [STATE.finish.progress, STATE.finish.rightCol.curName];

    if (progress === 1) return 0;

    let resultXOffset = 0;
    if (curName < position - 1) {
      ctx.globalAlpha = 0;
      resultXOffset = xOffset;
    } else if (curName < position) {
      ctx.globalAlpha = 1 - (position - curName);
      resultXOffset = xOffset * (position - curName);
    }

    return resultXOffset;
  };
  const deselectDriver = (gridPos) => {
    for (let i = 0; i < selectedDrivers.length; i++) {
      if (selectedDrivers[i] === gridPos) selectedDrivers.splice(i, 1);
    }
  };
  const translateField = (ctx) => {
    // Laptime finish
    if (STATE.ride.progress === 1 && STATE.type === LAPTIME) {
      const offset =
        -(size.startWidth + size.lapWidth * race.lapTotal + size.finishWidth - size.fieldWidth) -
        size.laptimeFinishWidth * STATE.finish.laptimeFinish.replaceProgress;
      ctx.translate(offset, 0);
      return;
    }
    if (STATE.ride.x > size.fieldWidth / 2) {
      if (STATE.ride.x <= size.startWidth + size.lapWidth * race.lapTotal + size.finishWidth - size.fieldWidth / 2) {
        const offset = size.fieldWidth / 2 - STATE.ride.x;
        ctx.translate(offset, 0);
      } else {
        const offset = -size.startWidth - size.lapWidth * race.lapTotal - size.finishWidth + size.fieldWidth;
        ctx.translate(offset, 0);
      }
    }
  };
  const getLapNumber = () => {
    const getNumber = (next = false, withCar = false) => {
      let number = (STATE.ride.x - size.startWidth - (withCar ? 0 : carSize.width)) / size.lapWidth;
      next ? (number = Math.ceil(number)) : (number = Math.floor(number));
      number <= 0 && (number = 0);
      number > race.lapTotal && (number = race.lapTotal);
      return number;
    };

    return {
      withCar: {
        curLap: getNumber(false, true),
        nextLap: getNumber(true, true),
      },
      withoutCar: { curLap: getNumber(), nextLap: getNumber(true) },
    };
  };
  const downloadScreenshot = () => {
    const screen = document.createElement("canvas");
    screen.width = size.width;
    screen.height = size.height;
    const ctx = createHiDPICanvas(size.width, size.height, screen).getContext("2d");

    updScreen(ctx);

    const link = document.createElement("a");
    const image = screen.toDataURL("image/jpeg", 1.0).replace("image/png", "image/octet-stream");
    link.download = "race.jpg";
    link.href = image;
    link.click();
    link.remove();
  };
  const updScreen = (ctx, headerHeight = 0) => {
    ctx.drawImage(rowsRef.current, 0, headerHeight + headHeight, size.width, size.fieldHeight);
    ctx.drawImage(fieldRef.current, 0, headerHeight + headHeight, size.width, size.fieldHeight);
    ctx.drawImage(leftRef.current, 0, headerHeight + headHeight, size.lColWidth + 20, size.fieldHeight);
    ctx.drawImage(
      rightRef.current,
      size.width - size.rColWidth - 20,
      headerHeight + headHeight,
      size.rColWidth + 20,
      size.fieldHeight
    );
    ctx.drawImage(redHeadRef.current, 0, headerHeight, size.width, headHeight);
    return STATE.progress;
  };
  const record = async () => {
    recordWidget({
      beforeRecord: setRecordSettings,
      afterRecord: resetRecordSettings,
      canvasesBlock: canvasesDivRef.current,
      span: recordingSpanRef.current,
      countryFlag: race.countryFlag,
      title: race.title.shortTitle,
      logoSrc: logo,
      BGColor,
      mainColor,
      ffl,
      replay,
      updCanvas: (ctx, header, footer) => updScreen(ctx, header, footer),
    });
  };

  const clickOnCanvas = (e) => {
    const canvasPos = getCoords(canvasesDivRef);
    const headerPos = getCoords(redHeadRef);

    const x = e.pageX - Math.ceil(canvasPos.left),
      y = e.pageY - Math.ceil(canvasPos.top);
    const hx = e.pageX - Math.ceil(headerPos.left),
      hy = e.pageY - Math.ceil(headerPos.top);

    if (x < size.lColWidth && hy > headHeight) clickOnDriver(y);
    const checkY = headHeight - 8;
    if (hx > columnsX.lcheck && hx < columnsX.lcheck + checkSize && hy > checkY - checkSize && hy < checkY)
      clickOnMainCheckBox();
  };
  const clickOnMainCheckBox = () => {
    if (selectedDrivers.length < driversCount) {
      selectedDrivers.length = 0;
      for (let i = 1; i <= driversCount; i++) selectedDrivers.push(i);
    } else selectedDrivers.length = 0;
    forcedRedraw();
  };
  const clickOnDriver = (y) => {
    const pos = Math.ceil((y - headHeight) / delY);
    if (selectedDrivers.includes(pos)) {
      deselectDriver(pos);
    } else selectedDrivers.push(pos);

    forcedRedraw();
  };
  const myDown = (e) => {
    const headerPos = getCoords(redHeadRef);
    const y = (isTouch ? e.touches[0].pageY : e.pageY) - Math.ceil(headerPos.top);
    if (y >= timeLineY - 12 && y <= timeLineY + 12) {
      STATE.dragop = true;
      redHeadRef.current.onmouseleave = myUp;
      if (isTouch) {
        redHeadRef.current.ontouchmove = myMove;
        redHeadRef.current.ontouchend = myUp;
      } else {
        redHeadRef.current.onmousemove = myMove;
        redHeadRef.current.onmouseup = myUp;
      }
      myMove(e);
    }
  };
  const myMove = (e) => {
    const headerPos = getCoords(redHeadRef);
    const x = (isTouch ? e.touches[0].pageX : e.pageX) - Math.ceil(headerPos.left);
    if (x <= size.width - timeLinePadding && x >= timeLinePadding) {
      const progress = (x - timeLinePadding) / (size.width - timeLinePadding * 2);
      STATE.timePassed = STATE.animDuration * progress;
      updSTATE(STATE.timePassed, STATE);
      forcedRedraw();
    }
  };
  const myUp = () => {
    if (STATE.dragop) {
      redHeadRef.current.onmouseleave = null;
      redHeadRef.current.ontouchmove = null;
      redHeadRef.current.ontouchend = null;
      redHeadRef.current.onmousemove = null;
      redHeadRef.current.onmouseup = null;
      STATE.dragop = false;
      lastFrameDate = Date.now();
    }
  };
  const showLegend = () => {
    isLegendShown = !isLegendShown;
    legendImgRef.current.style.display = isLegendShown ? "block" : "none";
  };

  // Customization
  const parseCustomizeBlock = (type, value) => {
    switch (type) {
      case "mainColor":
        changeMainColor(value);
        break;
      case "headerColor":
        changeTimeLineColor(value);
        break;
      case "BGColor":
        changeBGColor(value);
        break;
      case "textColor":
        changeTextColor(value);
        break;
      case "FontFamily":
        changeFontFamily(value);
        break;
      case "logo":
        logo = value;
        break;
      default:
        break;
    }
  };
  const changeMainColor = (color) => {
    mainColor = color;
    dataByRef.current.style.background = color;
    !STATE.playing && forcedRedraw();
  };
  const changeTimeLineColor = (color) => {
    headerColor = color;
    !STATE.playing && forcedRedraw();
  };
  const changeBGColor = (color) => {
    BGColor = color;
    grid = getGrid(BGColor);
    forcedRedraw();
  };
  const changeTextColor = (color) => {
    textColor = color;
    !STATE.playing && forcedRedraw();
  };
  const changeFontFamily = (font) => {
    ffl = font + ", sans-serif";
    ffr = font + ", sans-serif";
    setContexts();
    setTimeout(forcedRedraw, 200);
  };
  const getThemeData = () => {
    return {
      mainColor,
      headerColor,
      BGColor,
      textColor,
      logo,
      font: ffr.split(", ")[0].replace(" ", "+"),
    };
  };

  const updSize = () => {
    // if (size.visibleWidth === getWidths().visibleWidth) return

    size.windowWidth = getWidths().windowWidth;
    size.visibleWidth = getWidths().visibleWidth;

    marginLeft = size.windowWidth > 1000 ? 15 : 0;
    marginRight = 1; //Чтобы не появлялась горизонтальная прокрутка

    size.lColWidth = size.windowWidth > 1000 ? 170 : 100;
    size.rColWidth = size.windowWidth > 1000 ? 390 : size.windowWidth > 700 ? 120 : 60;

    size.widthWithDataBy = size.visibleWidth - marginLeft - marginRight;
    size.width = size.widthWithDataBy - (size.windowWidth > 1000 ? 50 : 0);

    size.fieldWidth = size.width - size.lColWidth - size.rColWidth;
    size.lapWidth = Math.max(50, (size.fieldWidth - size.startWidth - size.finishWidth) / race.lapTotal);

    columnsX = getColumnsX();

    canvasesDivRef.current.style.width = size.widthWithDataBy + "px";
    canvasesDivRef.current.style.marginLeft = marginLeft + "px";
    canvasesDivRef.current.style.height = size.height + "px";

    columnsX = getColumnsX();

    setContexts();
  };
  const setRecordSettings = (sv = { w: 1920, h: 1080 }) => {
    STATE.recording = true;

    size.width = sv.w;
    size.height = sv.h;
    delY = Math.round(size.height / (driversCount + 2));
    headHeight = delY * 2;
    size.fieldHeight = size.height - headHeight;

    carSize = getCarSize();
    flagSize = getFlagSize();
    iconSize = getIconSize();
    checkSize = getCheckSize();

    columnsX.lcheck = -100;
    columnsX.lflag = (delY / 2) * 3;
    columnsX.lname = columnsX.lflag + flagSize.width + 10;

    columnsX.rpos = (delY / 2) * 3 + 30;
    columnsX.rtime = columnsX.rpos + (delY / 2) * 2 + 10;
    columnsX.rpts = columnsX.rtime + (delY / 2) * 6 + 20;
    columnsX.rpit = columnsX.rpts + (delY / 2) * 2 + 10;
    columnsX.rmove = columnsX.rpit + (delY / 2) * 2 + 10;

    size.headLapsLineHeight = delY / 2 + 15;
    size.startWidth = 10 + iconSize + 5 + carSize.width + 5 + 10;
    size.finishWidth = 10 + 5 + carSize.width + 20;
    size.laptimeFinishWidth = 100 + carSize.width;
    size.lColWidth = columnsX.lname + (delY / 2) * 4;
    size.rColWidth = columnsX.rmove + (delY / 2) * 3 + 10;

    size.widthWithDataBy = size.width + 50;

    size.fieldWidth = size.width - size.lColWidth - size.rColWidth;
    size.lapWidth = Math.max(50, carSize.width, (size.fieldWidth - size.startWidth - size.finishWidth) / race.lapTotal);

    canvasesDivRef.current.style.width = size.widthWithDataBy + "px";
    canvasesDivRef.current.style.marginLeft = marginLeft + "px";
    canvasesDivRef.current.style.height = size.height + "px";

    grid = getGrid(BGColor);
    shadows = getShadows();
    STATERideXStart = 15 + iconSize + carSize.width;
    setContexts(false);
    for (const key of Object.keys(ctxs)) {
      ctxs[key].font = `normal 600 normal ${delY / 2}px ${ffl}`;
    }
    forcedRedraw();
  };
  const resetRecordSettings = () => {
    STATE.recording = false;
    headHeight = 70;
    delY = 30;
    grid = getGrid(BGColor);
    carSize = getCarSize();
    flagSize = getFlagSize();
    iconSize = getIconSize();
    checkSize = getCheckSize();

    size.headLapsLineHeight = 30;
    size.height = headHeight + delY * driversCount;
    size.fieldHeight = delY * driversCount;
    size.startWidth = 10 + iconSize + 5 + carSize.width + 5 + 10;
    size.finishWidth = 10 + 5 + carSize.width + 20;
    size.laptimeFinishWidth = 100 + carSize.width;

    columnsX = getColumnsX();
    shadows = getShadows();
    STATERideXStart = 15 + iconSize + carSize.width;
    updSize();
    setContexts(false);
    for (const key of Object.keys(ctxs)) {
      ctxs[key].font = `normal 600 normal ${delY / 2}px ${ffl}`;
    }

    forcedRedraw();
  };
  const cancelFrameUpdStateRequestFrame = () => {
    if (STATE.playing) window.cancelAnimationFrame(nextFrameID);
    STATE.timePassed = Math.min(STATE.animDuration, STATE.timePassed + 1);
    updSTATE(STATE.timePassed, STATE);
    forcedRedraw();
    lastFrameDate = Date.now();

    window.requestAnimationFrame(draw);
  };
  const zoom = (isIn) => {
    if (isIn) {
      size.lapWidth = size.lapWidth + 5;
    } else {
      size.lapWidth = size.lapWidth - 5;
    }

    zoomOutBtnRef.current.disabled = size.lapWidth < carSize.width;
    zoomInBtnRef.current.disabled = size.lapWidth > 100;

    cancelFrameUpdStateRequestFrame();
  };
  const changeSpeed = (isFaster) => {
    const step = 0.25;
    const max = 3;
    const min = -0.5;

    speed += isFaster ? step : -step;
    speed = Math.min(max, Math.max(min, speed));

    speedUpBtnRef.current.disabled = speed === max;
    speedDownBtnRef.current.disabled = speed === min;
  };
  const rewind = (isForward = true) => {
    if (isForward && STATE.finish.progress === 1) return;
    const timeDel =
      STATE.ride.animDuration /
      ((size.startWidth - STATERideXStart + size.lapWidth * race.lapTotal + size.finishWidth - 20) / size.lapWidth);
    STATE.timePassed += isForward ? timeDel : -timeDel;
    if (STATE.timePassed < 0) STATE.timePassed = 0;
    if (!STATE.playing) {
      updSTATE(STATE.timePassed, STATE);
      forcedRedraw();
    }
  };

  const getCurLapForDriver = (driver, withCar = false) => {
    let curLap;
    if (STATE.type === POSITION) curLap = STATE.ride.curLaps[withCar ? "withCar" : "withoutCar"].curLap;
    else {
      if (driver.time <= STATE.ride.curTime) curLap = driver.lastLap;
      else curLap = driver.data.find((l) => l.time > STATE.ride.curTime).lap - 1;
    }
    // driver.code === 'LEC' && console.log(STATE.ride.curTime, curLap)
    return curLap;
  };
  const retirementCheck = (driver) => {
    if (STATE.ride.curLaps.withoutCar.nextLap === 0) return false;
    if (STATE.type === POSITION) {
      return driver.retirement !== undefined && driver.retirement.x <= STATE.ride.curLaps.withoutCar.curLap;
    } else {
      return driver.retirement !== undefined && driver.time <= STATE.ride.curTime;
    }
  };
  const getSegmentLapCord = (driver, prevLap, nextLap) => {
    const prevPos = driver.data[prevLap].pos;
    const nextPos = driver.data[nextLap].pos;

    const prevX = size.lColWidth + size.startWidth + size.lapWidth * prevLap;
    const prevY = delY * (prevPos - 1) + delY / 2;

    const nextX = size.lColWidth + size.startWidth + size.lapWidth * nextLap;
    const nextY = delY * (nextPos - 1) + delY / 2;

    // driver.code === 'HAM'&&console.log(nextX - size.lColWidth, STATE.ride.x - carSize.width)

    if (nextX - size.lColWidth < STATE.ride.x - carSize.width) return { prevX, prevY, nextX, nextY };

    const fullLine = getLineObj(prevX, prevY, nextX, nextY);
    const curStateX = size.lColWidth + STATE.ride.x - carSize.width;
    const segmentCord = lineSegmentPoint(fullLine, curStateX);
    return { prevX, prevY, nextX: segmentCord.x, nextY: segmentCord.y };
  };
  const getPositionCord = (driver) => {
    let x, y;

    // Before grid
    if (STATE.ride.curLaps.withoutCar.nextLap === 0) {
      if (STATE.ride.x < 15 + iconSize + carSize.width) x = size.lColWidth + 15 + iconSize;
      else x = size.lColWidth + STATE.ride.x - carSize.width;
      y = delY * (driver.grid - 1) + delY / 2 - carSize.height / 2;
      return { x, y };
    }

    // Before finish
    if (STATE.ride.curLaps.withoutCar.curLap !== race.lapTotal) {
      const curLap = STATE.ride.curLaps.withoutCar.curLap;
      const nextLap = STATE.ride.curLaps.withoutCar.nextLap;

      if (driver.data[nextLap] === undefined)
        return {
          x: size.lColWidth + size.startWidth + size.lapWidth * (driver.data.length - 1),
          y: delY * (driver.data[driver.data.length - 1].pos - 1) + delY / 2 - carSize.height / 2,
        };

      const cords = getSegmentLapCord(driver, curLap, nextLap);
      x = cords.nextX;
      y = cords.nextY - carSize.height / 2;

      return { x, y };
    }

    // After finish
    x = size.lColWidth + STATE.ride.x - carSize.width;
    y = delY * (driver.data[driver.data.length - 1].pos - 1) + delY / 2 - carSize.height / 2;

    return { x, y };
  };
  const getLaptimeFinishPos = (x) => {
    let progress = STATE.finish.laptimeFinish.moveToGridProgress;
    const remain = size.lColWidth + size.startWidth + size.lapWidth * race.lapTotal + 20 - x;
    return x + remain * progress;
  };
  const getLaptimeCord = (driver) => {
    // Before grid
    if (STATE.ride.curTime === 0) {
      if (STATE.ride.x < 15 + iconSize + carSize.width) return size.lColWidth + 15 + iconSize;
      else return size.lColWidth + STATE.ride.x - carSize.width;
    }

    if (STATE.ride.curTime >= driver.data[driver.data.length - 1].time && driver.retirement !== undefined) {
      return size.lColWidth + size.startWidth + size.lapWidth * (driver.data.length - 1);
    }

    // Before finish
    if (driver.time <= STATE.ride.curTime)
      return getLaptimeFinishPos(size.lColWidth + size.startWidth + size.lapWidth * race.lapTotal);
    const nextLap = driver.data.find((lap) => lap.time > STATE.ride.curTime);
    const prevLap = driver.data[nextLap.lap - 1];

    const delTime = (STATE.ride.curTime - prevLap.time) / (nextLap.time - prevLap.time);
    const startX = size.lColWidth + size.startWidth;
    let x = startX + size.lapWidth * prevLap.lap + size.lapWidth * delTime;

    // Finish pos
    if (STATE.ride.progress === 1) return getLaptimeFinishPos(x);

    // driver.code === 'HUL'&&console.log(nextLap.x,nextLap.time, prevLap.x,prevLap.time, STATE.ride.curTime, x)
    return x;
  };

  const drawRows = (ctx = ctxs.rowsCTX, x = 0, y = 0, w = size.width, h = size.fieldHeight) => {
    ctx.fillStyle = ctx.createPattern(getRowsPattern(delY, BGColor), "repeat");
    ctx.fillRect(x, y, w, h);
  };
  const drawTimeLine = () => {
    const ctx = ctxs.redHeadCTX;

    const x1 = timeLinePadding;
    const y = timeLineY;
    const x2 = size.width - timeLinePadding;
    const lineWidth = size.width - timeLinePadding * 2;

    ctx.strokeStyle = headerColor;
    ctx.fillStyle = headerColor;
    ctx.lineWidth = 2;

    ctx.beginPath();
    ctx.moveTo(x1, 20);
    ctx.lineTo(x2, y);
    ctx.stroke();

    // traffic light
    ctx.save();
    const tlr = 3;
    // const tlx = size.lColWidth + size.fieldWidth / 2 - tlr * 4
    const tlx =
      timeLinePadding +
      lineWidth * ((STATE.driverPresent.animDuration + STATE.trafficLight.animDuration) / STATE.animDuration) -
      tlr * 10;
    const tly = timeLineY - tlr;

    ctx.fillStyle = mainColor;
    ctx.fillRect(tlx - 2 * tlr, tly - tlr, 12 * tlr, 6 * tlr);

    ctx.fillStyle = "#000";
    ctx.strokeStyle = "#000";
    ctx.lineWidth = 0.5;
    for (let i = 0; i < 5; i++) {
      if (STATE.trafficLight.phase - 1 >= i) ctx.fillStyle = "#f00";
      if (STATE.trafficLight.phase >= 6) ctx.fillStyle = "#0f0";
      ctx.beginPath();
      ctx.arc(tlx + tlr * 2 * i, tly, tlr, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(tlx + tlr * 2 * i, tly + tlr * 2, tlr, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#000";
    }
    ctx.restore();

    // flag
    const fw = 18;
    const fh = 20;
    const fx = timeLinePadding + lineWidth * ((STATE.animDuration - STATE.finish.animDuration) / STATE.animDuration);
    ctx.fillStyle = mainColor;
    ctx.fillRect(fx - 3, y - 3, fw + 6, 6);
    ctx.drawImage(icons.gridFlag, fx, y - fh / 2, fw, fh);

    // marker
    const r = 12;
    const progress = STATE.timePassed / STATE.animDuration;
    const mx = Math.round(x1 + (x2 - x1) * progress);
    ctx.fillStyle = headerColor;
    ctx.beginPath();
    ctx.arc(mx, y, r, 0, 2 * Math.PI);
    ctx.fill();
    ctx.fillStyle = mainColor;
    ctx.beginPath();
    ctx.arc(mx, y, r - 2, 0, 2 * Math.PI);
    ctx.fill();
    ctx.fillStyle = headerColor;
    ctx.beginPath();
    ctx.arc(mx, y, r - 4, 0, 2 * Math.PI);
    ctx.fill();
  };
  const drawHeadLapsLine = (height) => {
    const ctx = ctxs.redHeadCTX;
    const shadows = getShadows(20, height);

    ctx.fillStyle = applyBrightness(mainColor, 30);
    ctx.fillRect(size.lColWidth, headHeight - height, size.fieldWidth, height);
    ctx.drawImage(shadows.right, size.lColWidth, headHeight - height, 20, height);
    ctx.drawImage(shadows.left, size.lColWidth + size.fieldWidth - 20, headHeight - height, 20, height);

    translateField(ctx);

    ctx.fillStyle = headerColor;
    ctx.textAlign = "center";
    const x = size.lColWidth + size.startWidth;
    ctx.fillText("GRID", x, headHeight - 10);
    for (let i = 1; i <= race.lapTotal; i++) {
      const x = size.lColWidth + size.startWidth + size.lapWidth * i;
      ctx.fillText(i.toString(), x, headHeight - 10);
    }

    const mx = size.lColWidth + STATE.ride.x;
    ctx.beginPath();
    ctx.moveTo(mx - 6, headHeight - height);
    ctx.lineTo(mx, headHeight - height + 6);
    ctx.lineTo(mx + 6, headHeight - height);
    ctx.fill();

    ctx.setTransform(STATE.recording ? 1 : ratio.value, 0, 0, STATE.recording ? 1 : ratio.value, 0, 0);
  };
  const drawRedHead = () => {
    const ctx = ctxs.redHeadCTX;
    ctx.globalAlpha = 1;

    ctx.clearRect(0, 0, size.width, headHeight);

    //Laps numbers
    drawHeadLapsLine(size.headLapsLineHeight);

    ctx.fillStyle = mainColor;
    ctx.fillRect(0, 0, size.lColWidth, headHeight);
    ctx.fillRect(size.lColWidth - 1, 0, size.fieldWidth + 2, headHeight - size.headLapsLineHeight);
    ctx.fillRect(size.lColWidth + size.fieldWidth, 0, size.rColWidth, headHeight);

    let y = headHeight - 10;
    ctx.fillStyle = headerColor;
    ctx.strokeStyle = headerColor;
    ctx.textAlign = "start";
    ctx.fillText("POS", columnsX.lpos, y);

    // Чекбокс
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(columnsX.lcheck, y - checkSize + 2);
    ctx.lineTo(columnsX.lcheck + checkSize, y - checkSize + 2);
    ctx.lineTo(columnsX.lcheck + checkSize, y + 2);
    ctx.lineTo(columnsX.lcheck, y + 2);
    ctx.lineTo(columnsX.lcheck, y - checkSize + 2);
    ctx.stroke();
    if (selectedDrivers.length === driversCount) {
      ctx.beginPath();
      ctx.moveTo(columnsX.lcheck + 2, y - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + checkSize / 2.5, y - 2);
      ctx.lineTo(columnsX.lcheck + checkSize - 2, y - checkSize / 2 - 2);
      ctx.stroke();
    }

    ctx.fillText("DRIVER", size.windowWidth > 1000 ? columnsX.lflag : columnsX.lname, y);

    const rColOffset = size.lColWidth + size.fieldWidth;
    ctx.fillText("DRIVER", rColOffset + columnsX.rname, y);
    ctx.fillText("POS", rColOffset + columnsX.rpos, y);
    const timeHeader = STATE.type === LAPTIME && STATE.ride.progress === 1 ? "BEST TIME" : "TIME";
    ctx.fillText(timeHeader, rColOffset + columnsX.rtime, y);
    if (STATE.ride.progress === 1) ctx.fillText("PTS", rColOffset + columnsX.rpts, y);
    ctx.fillText("PIT", rColOffset + columnsX.rpit, y);
    ctx.fillText("MOVE", rColOffset + columnsX.rmove, y);

    //Time line
    drawTimeLine();
  };
  const drawTrafficLight = () => {
    const ctx = ctxs.fieldCTX;
    ctx.fillStyle = "#000";
    ctx.strokeStyle = "#000";

    const r = Math.min(70, size.fieldWidth / 15);
    const padding = 5;
    const x = size.lColWidth + size.fieldWidth / 2 - r * 4;
    const y = size.height / 2 - r * 2;

    for (let i = 0; i < 5; i++) {
      if (STATE.trafficLight.phase - 1 >= i) ctx.fillStyle = "#f00";
      if (STATE.trafficLight.phase === 6) ctx.fillStyle = "#0f0";

      ctx.beginPath();
      ctx.arc(x + (r + padding) * 2 * i, y - padding, r, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      ctx.beginPath();
      ctx.arc(x + (r + padding) * 2 * i, y + padding + r * 2, r, 0, 2 * Math.PI);
      ctx.fill();
      ctx.stroke();

      ctx.fillStyle = "#000";
    }
  };
  const drawLeftCol = () => {
    const ctx = ctxs.leftCTX;
    ctx.clearRect(0, 0, size.lColWidth + 20, size.fieldHeight);
    drawRows(ctx, 0, 0, size.lColWidth, size.fieldHeight);

    ctx.fillStyle = textColor;
    ctx.strokeStyle = textColor;
    ctx.textBaseline = "middle";

    for (let i = 0; i < driversCount; i++) {
      const driver = race.drivers[i];
      const grid = driver.grid;
      const y = delY / 2 + delY * (grid - 1);

      if (STATE.trafficLight.progress === 1 && STATE.ride.progress !== 1) ctx.globalAlpha = 0.8;
      else ctx.globalAlpha = 1;

      setIncludedDriverAlpha(ctx, grid);

      // Установки для появления
      let xOffset = 0;
      if (STATE.trafficLight.progress !== 1) {
        xOffset = presentSettings(ctx, grid, -100);
      }

      // Позиция
      ctx.fillText(grid, columnsX.lpos + xOffset, y);
      // Чекбокс
      if (selectedDrivers.includes(grid)) {
        ctx.beginPath();
        ctx.moveTo(columnsX.lcheck + 2 + xOffset, y);
        ctx.lineTo(columnsX.lcheck + checkSize / 2.5 + xOffset, y + checkSize / 2 - 3);
        ctx.lineTo(columnsX.lcheck + checkSize - 2 + xOffset, y - checkSize / 2 + 4);
        ctx.stroke();
      }
      ctx.beginPath();
      ctx.moveTo(columnsX.lcheck + xOffset, y - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + checkSize + xOffset, y - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + checkSize + xOffset, y + checkSize - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + xOffset, y + checkSize - checkSize / 2);
      ctx.lineTo(columnsX.lcheck + xOffset, y - checkSize / 2);
      ctx.stroke();
      // флаг
      if (driver.flag?.img !== undefined) {
        ctx.drawImage(
          driver.flag.img,
          columnsX.lflag + xOffset,
          y - flagSize.height / 2,
          flagSize.width,
          flagSize.height
        );
      } else if (driver.flag?.code !== undefined) ctx.fillText(driver.flag.code, columnsX.lflag + xOffset, y);
      // Имя
      ctx.font = `normal 600 normal ${delY / 2}px ${ffr}`;
      if (driver.code === undefined) ctx.fillText(driver.name, columnsX.lname + xOffset, y);
      else ctx.fillText(driver.code, columnsX.lname + xOffset, y);
      ctx.font = `normal 600 normal ${delY / 2}px ${ffl}`;
    }

    ctx.globalAlpha = 1;
    ctx.drawImage(shadows.right, size.lColWidth, 0, 20, size.fieldHeight);
  };
  const drawRightCol = () => {
    const ctx = ctxs.rightCTX;
    ctx.clearRect(0, 0, size.rColWidth, size.fieldHeight);
    drawRows(ctx, 20, 0, size.rColWidth, size.fieldHeight);

    const nameX = columnsX.rname + 20;
    const posX = columnsX.rpos + 20;
    const ptsX = columnsX.rpts + 20;
    const pitX = columnsX.rpit + 20 + ctx.measureText("PIT").width / 2;
    const moveX = columnsX.rmove + 20;
    const timeX = columnsX.rtime + 20;

    ctx.textBaseline = "middle";
    ctx.fillStyle = textColor;

    const curLap = STATE.ride.curLaps.withCar.curLap;

    // x del for blink
    const xDel = Math.max(0, (STATE.ride.x - size.startWidth) % size.lapWidth);
    const alphaForBlink = 0.7 + 0.3 * (1 - Math.min(10, xDel) / 10);
    // console.log(1 - Math.min(20, xDel)/20)

    for (let i = 0; i < driversCount; i++) {
      const driver = race.drivers[i];

      ctx.globalAlpha = 1;
      if (STATE.trafficLight.progress === 1 && STATE.ride.progress !== 1) ctx.globalAlpha = alphaForBlink;
      setIncludedDriverAlpha(ctx, driver.grid, 0.2);
      // Установки для появления
      let xOffset = 0;
      if (STATE.driverPresent.progress !== 1) xOffset = presentSettings(ctx, driver.grid, 100);
      if (curLap === race.lapTotal && STATE.finish.progress !== 1) {
        if (STATE.type === POSITION || STATE.ride.progress === 1) {
          xOffset = presentSettings(
            ctx,
            driver.lastLap === race.lapTotal ? driver.data[driver.data.length - 1].pos : driver.finishPos,
            100,
            false
          );
        } else xOffset = presentSettings(ctx, driver.grid, 100, false);
      }

      // console.log(curLap);

      const isRetire = driver.retirement !== undefined && driver.retirement.x < curLap;
      let y, pos;

      // Pos for retire
      if (isRetire) {
        if (STATE.ride.curLaps.withoutCar.curLap === race.lapTotal) {
          if (driver.classifiedStatus === "CLA") pos = driver.finishPos;
          else pos = "DNF";
        } else pos = "OUT";
      }
      // POSITION
      if (STATE.type === POSITION || STATE.ride.progress === 1) {
        // RETIRE
        if (isRetire) {
          y = delY / 2 + delY * (driver.finishPos - 1);
          driver.retirement.marker !== undefined &&
            ctx.drawImage(driver.retirement.marker, timeX + xOffset + 5, y - iconSize / 2, iconSize, iconSize);
          ctx.fillText(
            "Lap " + driver.retirement.x + " " + (driver.retirement.type !== undefined ? driver.retirement.type : ""),
            timeX + xOffset + iconSize + 10,
            y
          );
          //  NOT RETIRE
        } else {
          pos = driver.data[curLap].pos;
          y = delY / 2 + delY * (pos - 1);
        }
        // LAPTIME
      } else {
        y = delY / 2 + delY * (driver.grid - 1);
        if (isRetire) {
          driver.retirement.marker !== undefined &&
            ctx.drawImage(driver.retirement.marker, timeX + xOffset + 5, y - iconSize / 2, iconSize, iconSize);
          ctx.fillText(
            "Lap " + driver.retirement.x + " " + (driver.retirement.type !== undefined ? driver.retirement.type : ""),
            timeX + xOffset + iconSize + 10,
            y
          );
        } else pos = driver.data[curLap].pos;
      }

      ctx.font = `normal 600 normal ${delY / 2}px ${ffr}`;
      ctx.fillText(driver.code, nameX + xOffset, y); // Name
      ctx.font = `normal 600 normal ${delY / 2}px ${ffl}`;
      ctx.fillText(STATE.ride.progress === 1 ? driver.finishPos : pos, posX + xOffset, y); // Position

      if (!isRetire) {
        // Time
        if (STATE.type === POSITION) {
          if (curLap > 0 && pos === 1) {
            ctx.fillText(msToTime(driver.data[curLap].time), timeX + xOffset, y);
          } else {
            if (driver.data[curLap].lapsToLead === 0) {
              ctx.fillText("+" + msToTime(driver.data[curLap].gap), timeX + xOffset, y);
            } else ctx.fillText("+" + driver.data[curLap].lapsToLead + " lap", timeX + xOffset, y);
          }
        } else {
          if (STATE.ride.progress === 1) {
            ctx.fillText(msToTime(driver.bestTime), timeX + xOffset, y);
          } else {
            if (driver.data[curLap].absent) {
              ctx.fillText("-", timeX + xOffset, y);
            } else ctx.fillText(msToTime(driver.data[curLap].lapTime), timeX + xOffset, y);
          }
        }

        const pit = driver.data[curLap].pit;
        ctx.textAlign = "center";
        if (pit === 0) ctx.fillText("-", pitX + xOffset, y);
        // PIT
        else ctx.fillText(driver.data[curLap].pit, pitX + xOffset, y); // PIT
        ctx.textAlign = "start";

        if (curLap > 0) {
          const move = driver.grid - pos;
          const arrow = icons.arrows[move > 0 ? 0 : move < 0 ? 2 : 1];
          if (move > 0) ctx.fillStyle = "#008d36";
          else if (move < 0) ctx.fillStyle = "#ea0029";
          ctx.drawImage(arrow, moveX + xOffset, y - 8, 16, 16);
          ctx.fillText(move.toString(), moveX + xOffset + 16 + 8, y); // Move
          ctx.fillStyle = textColor;
        } else {
          ctx.fillText("      -", moveX + xOffset, y); // Move
        }

        if (STATE.ride.progress === 1) ctx.fillText(driver.pts, ptsX + xOffset, y); // PTS
      }
      // Cups
      if (STATE.ride.curLaps.withoutCar.curLap === race.lapTotal && driver.finishPos < 4) {
        const cupSize = iconSize / 2;
        const cx = size.windowWidth > 1000 ? posX + xOffset - cupSize - 5 : posX + xOffset + 15;
        ctx.drawImage(icons.cups[driver.finishPos - 1], cx, y - cupSize / 2, cupSize, cupSize);
      }
    }

    ctx.globalAlpha = 1;
    ctx.drawImage(shadows.left, 0, 0, 20, size.fieldHeight);
  };

  const drawCars = () => {
    const ctx = ctxs.fieldCTX;

    for (let i = 0; i < driversCount; i++) {
      const driver = race.drivers[i];

      setIncludedDriverAlpha(ctx, driver.grid, 0.1);

      let x, y;

      drawMarkers(driver);

      // Retirement drivers
      if (retirementCheck(driver)) {
        let xOffset = 0;
        if (driver.retirement.marker !== undefined || driver.pitstops.includes(driver.lastLap)) {
          if (STATE.ride.curLaps.withoutCar.curLap <= driver.retirement.x) {
            xOffset = Math.min(iconSize / 2 + 5, size.lapWidth * STATE.ride.delLap);
          } else xOffset = iconSize / 2 + 5;
        }
        x = size.lColWidth + size.startWidth + size.lapWidth * driver.retirement.x;
        let my;

        if (STATE.type === POSITION) {
          y = delY * (driver.data[driver.data.length - 1].pos - 1) + delY / 2 - carSize.height / 2;
          my = delY * (driver.data[driver.data.length - 1].pos - 1) + delY / 2 - iconSize / 2;
        } else {
          y = delY * (driver.grid - 1) + delY / 2 - carSize.height / 2;
          my = delY * (driver.grid - 1) + delY / 2 - iconSize / 2;
        }

        let mx = x;
        if (
          driver.markers.find((m) => m.x === driver.retirement.x && m.x !== 0) ||
          driver.pitstops.find((p) => p === driver.retirement.x && p !== 0)
        )
          mx += -1.5 * iconSize - 3;
        else mx += -iconSize / 2;
        driver.retirement.marker !== undefined && ctx.drawImage(driver.retirement.marker, mx, my, iconSize, iconSize);

        ctx.drawImage(driver.carImg, x + xOffset, y, carSize.width, carSize.height);
        continue;
      }

      if (STATE.type === POSITION) {
        const cord = getPositionCord(driver);
        x = cord.x;
        y = cord.y;
      } else {
        if (STATE.finish.laptimeFinish.moveToGridProgress === 1) drawLaptimeFinish(driver);
        x = getLaptimeCord(driver);
        y = delY * (driver.grid - 1) + delY / 2 - carSize.height / 2;
      }
      // driver.code === 'GRO' && console.log(x)
      ctx.drawImage(driver.carImg, x, y, carSize.width, carSize.height);
    }

    ctx.globalAlpha = 1;
  };
  const fillLineWithBorderIfNeed = (ctx, color) => {
    if (color === "255,255,255") {
      ctx.save();
      ctx.lineWidth = 8;
      ctx.strokeStyle = "#000000";
      ctx.stroke();
      ctx.lineWidth = 6;
      ctx.strokeStyle = "#FFFFFF";
      ctx.stroke();
      ctx.restore();
    } else ctx.stroke();
  };
  const drawLaptimeLine = (driver) => {
    const ctx = ctxs.fieldCTX;
    const x = getLaptimeCord(driver);
    const y = delY * (driver.grid - 1) + delY / 2;

    ctx.beginPath();
    ctx.moveTo(size.lColWidth + size.startWidth, y);
    ctx.lineTo(x, y);
    fillLineWithBorderIfNeed(ctx, driver.color);
  };
  const drawPositionLine = (driver) => {
    const ctx = ctxs.fieldCTX;
    const baseX = size.lColWidth + size.startWidth;

    ctx.beginPath();
    ctx.moveTo(baseX, delY * driver.grid - delY / 2);
    for (let i = 1; i <= STATE.ride.curLaps.withoutCar.curLap; i++) {
      if (i < STATE.ride.curLaps.leftLap) continue;
      if (driver.data.length - 1 < i) break;
      const x = baseX + size.lapWidth * i;
      const y = delY * driver.data[i].pos - delY / 2;
      ctx.lineTo(x, y);
    }

    if (STATE.ride.curLaps.withoutCar.curLap !== race.lapTotal) {
      const cord = getPositionCord(driver);
      const x = cord.x;
      const y = cord.y + carSize.height / 2;
      // driver.code === 'STR' && console.log(x, y)
      ctx.lineTo(x, y);
    }
    fillLineWithBorderIfNeed(ctx, driver.color);
  };
  const drawLines = () => {
    if (STATE.ride.x < size.startWidth) return;
    const ctx = ctxs.fieldCTX;

    ctx.lineWidth = 6;
    ctx.globalAlpha = 1;

    for (let i = 0; i < driversCount; i++) {
      const driver = race.drivers[i];
      ctx.strokeStyle = "rgba(" + driver.color + ", 0.6)";
      setIncludedDriverAlpha(ctx, driver.grid, 0.1);

      if (STATE.type === LAPTIME) drawLaptimeLine(driver);
      else drawPositionLine(driver);
    }

    ctx.globalAlpha = 1;
  };
  const drawMarkers = (driver) => {
    if (driver.markers.length === 0 && driver.pitstops === 0) return;
    const ctx = ctxs.fieldCTX;

    const curLap = getCurLapForDriver(driver);
    const check = (lap, img) => {
      return lap <= curLap && curLap > 0 && lap > STATE.ride.curLaps.leftLap && img !== undefined;
    };

    const draw = (lap, img) => {
      if (!check(lap, img)) return;
      const x = size.lColWidth + size.startWidth + size.lapWidth * lap - iconSize / 2;
      let y = -delY / 2 - iconSize / 2;
      if (STATE.type === POSITION) y += delY * driver.data[lap].pos;
      else y += delY * driver.grid;
      ctx.drawImage(img, x, Math.round(y), iconSize, iconSize);
    };

    for (const marker of driver.markers) {
      if (marker.x === 0 && marker.type.includes("Tyre")) continue;
      draw(marker.x, icons[marker.type]);
    }

    for (const pit of driver.pitstops) draw(pit, icons.pitstop);
  };
  const drawLaptimeFinish = (driver) => {
    const ctx = ctxs.fieldCTX;

    ctx.strokeStyle = "rgba(" + driver.color + ", 0.4)";

    const startX = size.lColWidth + size.startWidth + size.lapWidth * race.lapTotal + 20;
    const startY = delY * (driver.grid - 1) + delY / 2;
    let y = startY;
    let x;

    ctx.beginPath();
    ctx.moveTo(startX, startY);

    if (STATE.finish.laptimeFinish.replaceProgress <= carSize.width / size.laptimeFinishWidth) {
      let prog = (size.laptimeFinishWidth * STATE.finish.laptimeFinish.replaceProgress) / carSize.width;
      prog = Math.min(1, Math.max(0, prog));
      x = startX + carSize.width * prog;
    } else {
      const prevX = startX + carSize.width;
      const nextX = prevX + size.laptimeFinishWidth - carSize.width;
      const nextY = delY * (driver.finishPos - 1) + delY / 2;

      const fullLine = getLineObj(prevX, startY, nextX, nextY);
      const curStateX = startX + size.laptimeFinishWidth * STATE.finish.laptimeFinish.replaceProgress;
      const segmentCord = lineSegmentPoint(fullLine, curStateX);

      x = segmentCord.x;
      y = segmentCord.y;

      ctx.lineTo(startX + carSize.width, startY);
    }

    ctx.lineTo(x, y);
    fillLineWithBorderIfNeed(ctx, driver.color);
    ctx.drawImage(driver.carImg, x, y - carSize.height / 2, carSize.width, carSize.height);
  };

  const drawField = () => {
    const ctx = ctxs.fieldCTX;
    ctx.clearRect(0, 0, size.width, size.fieldHeight);
    ctx.lineWidth = 1;

    translateField(ctx);

    //Start
    if (STATE.ride.x < size.fieldWidth) {
      for (const driver of race.drivers) {
        const sy = delY * (driver.grid - 1) + delY / 2;
        if (driver.markers.length > 0 || driver.tyreStrategy.length > 0) {
          setIncludedDriverAlpha(ctx, driver.grid, 0.2);
          let tyre = driver.markers.find((m) => m.x === 0 && m.type.includes("Tyre"));
          if (tyre === undefined) {
            tyre = driver.tyreStrategy[0];
          }
          if (tyre !== undefined) {
            ctx.drawImage(icons[tyre.type], size.lColWidth + 10, Math.round(sy - iconSize / 2), iconSize, iconSize);
          }
        }
        ctx.drawImage(
          driver.carImg,
          size.lColWidth + 15 + iconSize,
          Math.round(sy - carSize.height / 2),
          carSize.width,
          carSize.height
        );
      }
    }

    ctx.globalAlpha = 1;

    ctx.strokeStyle = "rgba(0,0,0,0.1)";
    for (let i = 1; i < race.lapTotal; i++) {
      if (i < STATE.ride.curLaps.leftLap) continue;
      const x = size.lColWidth + size.startWidth + size.lapWidth * i;
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, size.fieldHeight);
      ctx.stroke();
    }

    drawLines();

    // Start Grid
    if (0 >= STATE.ride.curLaps.leftLap) ctx.drawImage(grid, size.lColWidth + size.startWidth - 10, 0);
    // Finish Grid
    ctx.drawImage(grid, size.lColWidth + size.startWidth + size.lapWidth * race.lapTotal - 10, 0);

    drawCars();
    if (STATE.driverPresent.progress === 1 && STATE.trafficLight.progress !== 1) drawTrafficLight();

    ctx.setTransform(STATE.recording ? 1 : ratio.value, 0, 0, STATE.recording ? 1 : ratio.value, 0, 0);
  };

  const replay = () => {
    resetState();
    if (!STATE.playing) {
      lastFrameDate = Date.now();
      STATE.playing = true;
    }
    playBtnRef.current.checked = true;
  };
  const forcedRedraw = () => {
    drawRows();
    drawLeftCol();
    drawField();
    drawRightCol();
    drawRedHead();
  };
  const draw = () => {
    if (STATE.playing && !STATE.dragop && Date.now() - lastFrameDate > 1000 / 60) {
      updSTATE(STATE.timePassed, STATE);

      drawField();
      if (STATE.rightCol.needRerender) {
        drawRightCol();
        STATE.rightCol.needRerender = false;
      }
      drawRedHead();

      const delTime = Date.now() - lastFrameDate;
      STATE.timePassed += delTime + delTime * speed;
      if (STATE.timePassed > STATE.animDuration) STATE.timePassed = STATE.animDuration;
      lastFrameDate = Date.now();
    }
    nextFrameID = window.requestAnimationFrame(draw);
  };

  const setContexts = (useRatio = true) => {
    ctxs.rowsCTX = createHiDPICanvas(size.width, size.fieldHeight, rowsRef.current, useRatio).getContext("2d");
    ctxs.leftCTX = createHiDPICanvas(size.lColWidth + 20, size.fieldHeight, leftRef.current, useRatio).getContext("2d");
    ctxs.fieldCTX = createHiDPICanvas(size.width, size.fieldHeight, fieldRef.current, useRatio).getContext("2d");
    ctxs.rightCTX = createHiDPICanvas(size.rColWidth + 20, size.fieldHeight, rightRef.current, useRatio).getContext(
      "2d"
    );
    ctxs.redHeadCTX = createHiDPICanvas(size.width, headHeight, redHeadRef.current, useRatio).getContext("2d");
    for (const key of Object.keys(ctxs)) {
      ctxs[key].font = "normal 600 normal 16px " + ffl;
    }
  };
  useEffect(() => {
    console.log(race);
    window.onresize = () => onResize(updSize, forcedRedraw);
    if (isTouch) {
      redHeadRef.current.ontouchstart = myDown;
    } else {
      redHeadRef.current.onmousedown = myDown;
    }
    updSize(); // Могла появиться вертикальная прокрутка, поэтому 2 раза
    updSize();
    setFullAnimDur();
    forcedRedraw();
    lastFrameDate = Date.now();
    nextFrameID = window.requestAnimationFrame(draw);
    return () => window.cancelAnimationFrame(nextFrameID);
  }, []);

  const controlBtnsOptions = {
    replyOnClick: replay,

    plyOnClick: () => {
      STATE.playing = !STATE.playing;
      lastFrameDate = Date.now();
    },
    playDef: STATE.playing,
    playRef: playBtnRef,

    moveOnClick: rewind,
    // moveFRef: moveForwardRef,
    // moveBRef: moveBackRef,

    showZoom: true,
    zoomOnClick: zoom,
    zoomInRef: zoomInBtnRef,
    zoomOutRef: zoomOutBtnRef,

    speedOnClick: changeSpeed,
    speedUpRef: speedUpBtnRef,
    speedDownRef: speedDownBtnRef,

    showType: true,
    typeOnClick: () => {
      STATE.type === LAPTIME ? (STATE.type = POSITION) : (STATE.type = LAPTIME);
      localStorage.setItem("race_type", STATE.type);
      !STATE.playing && forcedRedraw();
    },
    typeDef: STATE.type === LAPTIME,
    typeLT: POSITION,
    typeRT: LAPTIME,
    typeLIMG: positionTypeImg,
    typeRIMG: laptimeTypeImg,
  };
  return (
    <div style={{ position: "relative", userSelect: "none" }}>
      <div className={styles.btnsRow}>
        <div className={styles.leftBtns}>
          <ControlButtons options={controlBtnsOptions} />
        </div>
        <div className={styles.rightBtns}>
          <ShareBtns
            title={race.title.name}
            showDownloadBtns={props.isConfig}
            clickOnScreen={downloadScreenshot}
            clickOnVideo={record}
          />
        </div>
      </div>
      <div>
        <div ref={canvasesDivRef} className={styles.canvases} onClick={(e) => clickOnCanvas(e)}>
          <canvas ref={rowsRef} className={styles.canvas + " " + styles.rowsCanvas} />
          <canvas ref={fieldRef} className={styles.canvas + " " + styles.fieldCanvas} />
          <canvas ref={leftRef} className={styles.canvas + " " + styles.leftCanvas} />
          <canvas ref={rightRef} className={styles.canvas + " " + styles.rightCanvas} />
          <canvas ref={redHeadRef} className={styles.canvas + " " + styles.redHeadCanvas} />
          <div ref={dataByRef} className={styles.dataBy} style={{ background: mainColor }}>
            <DataBy />
          </div>
          <img ref={legendImgRef} className={mainStyles.legendImg} src={legendImg} alt={"Legend"} />
        </div>
        <span ref={recordingSpanRef} className={mainStyles.recordingSpan}>
          Recording
        </span>
      </div>

      {/* legend btn */}
      <button onClick={showLegend} className={mainStyles.legendBtn}>
        KEY
      </button>

      {/* bot share btns */}
      <div className={styles.botShareBtns}>
        <ShareBtns
          title={race.title.name}
          showDownloadBtns={props.isConfig}
          clickOnScreen={downloadScreenshot}
          clickOnVideo={record}
        />
      </div>

      {/* Customize block */}
      {props.isConfig && (
        <CustomizeBlock def={getThemeData()} onChange={parseCustomizeBlock} getThemeData={getThemeData} />
      )}
    </div>
  );
};

export default React.memo(Race, (p, n) => {
  // console.log(p.race.title.name, n.race.title.name)
  return p.race.title.name === n.race.title.name;
});
